import esriConfig from '@arcgis/core/config.js';
import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { createLayerFromUrl } from '@rsos/maps/providers/esri/layers/createLayerFromUrl';
import { createWMSLayer } from '@rsos/maps/providers/esri/layers/createWMSLayer';
import { createWMTSLayer } from '@rsos/maps/providers/esri/layers/createWMTSLayer';
import checkGISServicesAccess from '@rsos/utils/checkGISServicesAccess';
import { captureExceptionWithScope } from '@rsos/utils/sentry';
import usePortalType from '@rsos/utils/usePortalType';
import checkLayerVisiblity from '../utils/checkLayerVisiblity';
import { getGISLayerID } from '../utils/getGISLayerID';
import { notifyEsriMapLayerError } from '../utils/notifyEsriMapLayerError';
import useGISHelpers from './useGISHelpers';

/**
 * Request interceptor handler when creating a new GIS service layer from url.
 * @param {String} url - Url to incercept.
 * @param {String} token - Token parameter to be added to the url.
 * @returns Array
 */
const handleRequestInterceptor = (url, token) => {
  esriConfig.request.interceptors.push({
    urls: url,
    before: params => {
      params.requestOptions.query = params.requestOptions.query || {};

      params.requestOptions.query.token = token;
    },
    error: () => {
      // Suppress request interceptor error.
    },
  });
};

/**
 * An Esri map GIS services layers hook that processes GIS supplemental, WMS, and WMTS services.
 * GIS map layers are controlled via the MapLayersControl component.
 * @param {Object} map - The Esri map object
 */
const useGISServiceLayers = map => {
  const { isPremiumUser } = usePortalType();

  const dispatch = useDispatch();

  const { hasEsriMapAccess } = useGISHelpers();

  const hasGISServicesAccess =
    useSelector(state =>
      checkGISServicesAccess({
        psaps: state.psaps,
        sinatra: state.sinatra,
      }),
    ) &&
    isPremiumUser &&
    hasEsriMapAccess;

  const gisIdentities = useSelector(state => state.gisServices.gisIdentities);

  const basemapService = useSelector(state => state.gisServices.basemapService);

  const supplementalServices = useSelector(
    state => state.gisServices.supplementalServices,
  );

  const wmsServices = useSelector(state => state.gisServices.wmsServices);

  const wmtsServices = useSelector(state => state.gisServices.wmtsServices);

  const activeGISServices = useSelector(
    state => state.gisServices.activeGISServices,
  );

  const lastToggledGISService = useSelector(
    state => state.gisServices.lastToggledGISService,
  );

  const numClearAllClicked = useSelector(
    state => state.gisServices.numClearAllClicked,
  );

  const mapInstance = map?.mapInstance?.map;

  const esriMapLayers = mapInstance?.layers?.items;

  const [supplementalLayers, setSupplementalLayers] = useState([]);

  const [wmsLayers, setWMSLayers] = useState([]);

  const [wmtsLayers, setWMTSLayers] = useState([]);

  const captureLayerError = useCallback(
    (service, error) => {
      notifyEsriMapLayerError();

      const foundIdentity = gisIdentities.find(gisIdentity =>
        gisIdentity.gisServices.find(
          gisService => gisService.id === service.id,
        ),
      );

      captureExceptionWithScope(error, {
        extra: {
          errorMessage: error?.message,
          gisIdentity: {
            arcGISToken: foundIdentity?.arcGISToken,
            gisIdentityUrl: foundIdentity?.gisIdentityUrl,
          },
          gisService: service,
        },
      });
    },
    [gisIdentities],
  );

  /**
   * =======================================
   *  GIS SUPPLEMENTAL LAYERS CONFIGURATION
   * =======================================
   */

  // Create GIS supplemental layers on load and set the visible property to false.
  // The visible property is updated when the user toggles the layer via the map layers menu control.
  useEffect(() => {
    if (hasEsriMapAccess && supplementalServices.length) {
      const layers = [];

      supplementalServices.forEach(service => {
        handleRequestInterceptor(service.gis_service_url, service.token);

        const layerData = {
          apiKey: service?.token,
          id: getGISLayerID(service),
          url: service.gis_service_url,
          visible: false,
        };

        createLayerFromUrl(layerData)
          .then(layer => {
            if (layer?.loaded && !layers.includes(layer)) {
              const loadedLayer = checkLayerVisiblity(layer);

              layers.push({ layer: loadedLayer, service });
            }
          })
          .catch(error => {
            captureLayerError(service, error);
          });
      });

      setSupplementalLayers(layers);
    }
  }, [captureLayerError, hasEsriMapAccess, supplementalServices]);

  /**
   * ==========================
   *  WMS LAYERS CONFIGURATION
   * ==========================
   */

  // Create GIS WMS layers on load and set the visible property to false.
  // The visible property is updated when the user toggles the layer via the map layers menu control.
  useEffect(() => {
    if (hasEsriMapAccess && wmsServices.length) {
      const layers = [];

      wmsServices.forEach(service => {
        const layerData = {
          id: getGISLayerID(service),
          layerNames: service?.attributes?.sublayers || '',
          url: service.gis_service_url,
          visible: false,
        };

        createWMSLayer(layerData)
          .then(layer => {
            if (layer?.loaded && !layers.includes(layer)) {
              layers.push({ layer, service });
            }
          })
          .catch(error => {
            captureLayerError(service, error);
          });
      });

      setWMSLayers(layers);
    }
  }, [captureLayerError, hasEsriMapAccess, wmsServices]);

  /**
   * ===========================
   *  WMTS LAYERS CONFIGURATION
   * ===========================
   */

  // Create GIS WMTS layers on load and set the visible property to false.
  // The visible property is updated when the user toggles the layer via the map layers menu control.
  useEffect(() => {
    if (hasEsriMapAccess && wmtsServices.length) {
      const layers = [];

      wmtsServices.forEach(service => {
        // Unlike WMS layers which accepts an array of layer names, WMTS layer accepts a single
        // layer name that can be displayed on the map.
        const layerData = {
          id: getGISLayerID(service),
          layerName: service?.attributes?.sublayers?.[0] || '',
          url: service.gis_service_url,
          visible: false,
        };

        createWMTSLayer(layerData)
          .then(layer => {
            if (layer?.loaded && !layers.includes(layer)) {
              layers.push({ layer, service });
            }
          })
          .catch(error => {
            captureLayerError(service, error);
          });

        setWMTSLayers(layers);
      });
    }
  }, [captureLayerError, hasEsriMapAccess, wmtsServices]);

  // When an user toggles a GIS service layer via the map layers menu control, update the layer's
  // visible property.
  // https://developers.arcgis.com/javascript/latest/api-reference/esri-Map.html#findLayerById
  useEffect(() => {
    if (
      hasEsriMapAccess &&
      mapInstance &&
      activeGISServices &&
      Object.keys(lastToggledGISService).length
    ) {
      const toggledLayer = mapInstance.findLayerById(
        getGISLayerID(lastToggledGISService),
      );

      if (toggledLayer) {
        toggledLayer.visible = !toggledLayer.visible;
      }
    }
  }, [hasEsriMapAccess, mapInstance, activeGISServices, lastToggledGISService]);

  // If the clear all button was click in the map layers control,
  // set all the GIS supplemental service layers' visibility to false.
  useEffect(() => {
    if (hasGISServicesAccess && esriMapLayers && numClearAllClicked) {
      esriMapLayers.forEach(layer => {
        if (layer.id.startsWith(`gis-service`)) {
          layer.visible = false;
        }
      });
    }
  }, [
    dispatch,
    hasGISServicesAccess,
    esriMapLayers,
    basemapService,
    numClearAllClicked,
  ]);

  return [...supplementalLayers, ...wmsLayers, ...wmtsLayers];
};

export default useGISServiceLayers;
